import React, { useState } from "react";

import SvgPlus from "@uui/Plus";

import { Skeleton, Typography } from "@mui/material";

import BillingLinkButton from "@app/analytics/account/Billing/components/BillingLinkButton";
import BillingModuleError from "@app/analytics/account/Billing/components/BillingModuleError";
import {
  BillingCard,
  BillingCardActions,
  BillingCardContent,
  BillingCardHeader,
} from "@app/analytics/account/Billing/components/styles";
import usePaymentMethod from "@app/analytics/account/Billing/hooks/usePaymentMethod";
import { isFreePlan } from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import * as Events from "@app/analytics/tracking/events";
import { showToast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type { PaymentMethod as IPaymentMethod } from "@app/common/types";

import PaymentMethodDetails from "./PaymentMethodDetails";
import RemovePaymentConfirmationModal from "./RemovePaymentConfirmationModal";

const LoadingContent = () => (
  <>
    <Skeleton
      variant="rectangular"
      sx={{ height: "30px", width: "100%", borderRadius: "8px" }}
    />
    <BillingCardActions>
      <Skeleton
        variant="rectangular"
        sx={{ height: "50px", width: "300px", borderRadius: "8px" }}
      />
    </BillingCardActions>
  </>
);

const DefaultContent = ({
  paymentMethod,
  onRemove,
}: {
  paymentMethod: IPaymentMethod;
  onRemove: () => void;
}) => (
  <>
    <PaymentMethodDetails paymentMethod={paymentMethod} onRemove={onRemove} />
    <BillingCardActions>
      <BillingLinkButton to={ROUTES.BILLING_UPDATE}>
        Update payment method
      </BillingLinkButton>
    </BillingCardActions>
  </>
);

const EmptyContent = ({ inTrial }: { inTrial: boolean }) => {
  const route = inTrial ? ROUTES.BILLING_COMPARE_PLANS : ROUTES.BILLING_UPDATE;
  return (
    <>
      <Typography color="shades.neutral.600">No payment method</Typography>
      <BillingCardActions>
        <BillingLinkButton
          onClick={() => Events.addPaymentMethod()}
          to={route}
          startIcon={<SvgPlus />}
          variant="text"
          sx={{ color: "shades.neutral.600", fontWeight: "400" }}
        >
          Add payment method
        </BillingLinkButton>
      </BillingCardActions>
    </>
  );
};

const PaymentMethod = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { billingAccount, currentPlan, isTrial, scheduledPlan, subscription } =
    useBillingContext();
  const {
    paymentMethod,
    paymentMethodIsLoading,
    removePaymentMethod,
    isError,
  } = usePaymentMethod();

  const handleRemoveCard = () => {
    // if they're not paying us, there are no consequences of removing a payment method.
    if (isFreePlan(currentPlan.type)) {
      return handleConfirmRemoval();
    }

    setIsOpen(true);
  };

  const handleConfirmRemoval = () => {
    if (billingAccount?.id) {
      removePaymentMethod.mutate(undefined, {
        onSuccess: () => {
          showToast({ message: "Payment method removed", severity: "success" });
        },
        onError: () => {
          showToast({
            severity: "error",
            message:
              "There was an error removing your payment method. Please refresh the page or try again later.",
          });
        },
        onSettled: () => setIsOpen(false),
      });
    }
  };

  return (
    <>
      <BillingCard testId="billing-payment-method">
        <BillingCardHeader>
          <Typography variant="bodyPrimaryMed">Payment Method</Typography>
        </BillingCardHeader>
        <BillingCardContent>
          {paymentMethodIsLoading ? (
            <LoadingContent />
          ) : paymentMethod ? (
            <DefaultContent
              paymentMethod={paymentMethod}
              onRemove={handleRemoveCard}
            />
          ) : isError ? (
            <BillingModuleError entityLabel="payment method" />
          ) : (
            <EmptyContent inTrial={isTrial} />
          )}
        </BillingCardContent>
      </BillingCard>

      {paymentMethod && (
        <RemovePaymentConfirmationModal
          loading={removePaymentMethod.isPending}
          open={isOpen}
          onClose={() => setIsOpen(false)}
          onConfirm={handleConfirmRemoval}
          inTrialPeriod={subscription.inTrialPeriod}
          currentPeriodEnd={subscription.currentPeriodEnd}
          currentPlanLabel={currentPlan.label}
          hasScheduledPlan={Boolean(scheduledPlan)}
        />
      )}
    </>
  );
};

export default PaymentMethod;
